import { useMemo, useState } from "react";
import { fetchWithToken } from "../api/apiutils";

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

const isObject = (value) => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

const tryParseJson = (value) => {
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const formatKey = (key) => {
  return key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .trim();
};

const getValueColor = (value) => {
  if (typeof value === "number") {
    return "text-blue-700";
  }

  if (typeof value === "boolean") {
    return value ? "text-green-700" : "text-red-700";
  }

  return "text-gray-800";
};

// ─────────────────────────────────────────────────────────────
// Universal renderer
// ─────────────────────────────────────────────────────────────

const RenderValue = ({ data, depth = 0, title }) => {
  const parsed = tryParseJson(data);

  // null / undefined
  if (parsed === null || parsed === undefined) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-gray-400">
        null
      </div>
    );
  }

  // primitive
  if (
    typeof parsed === "string" ||
    typeof parsed === "number" ||
    typeof parsed === "boolean"
  ) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        {title && (
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {formatKey(title)}
          </div>
        )}

        <div
          className={`whitespace-pre-wrap break-words text-sm leading-7 ${getValueColor(
            parsed,
          )}`}
        >
          {String(parsed)}
        </div>
      </div>
    );
  }

  // array
  // array
  if (Array.isArray(parsed)) {
    return (
      <div className="space-y-3">
        {title && (
          <div className="text-sm font-bold uppercase tracking-wide text-gray-600">
            {formatKey(title)}
          </div>
        )}

        {parsed.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-400">
            Пустой массив
          </div>
        ) : (
          <div className="space-y-2">
            {parsed.map((item, index) => {
              const isPrimitive =
                typeof item === "string" ||
                typeof item === "number" ||
                typeof item === "boolean";

              return (
                <div
                  key={index}
                  className="rounded-xl border border-gray-200 bg-gray-50 p-3"
                >
                  {isPrimitive ? (
                    <div className="flex gap-2">
                      <span className="font-semibold text-gray-400">
                        {index + 1}.
                      </span>

                      <span className="whitespace-pre-wrap break-words text-sm text-gray-800">
                        {String(item)}
                      </span>
                    </div>
                  ) : (
                    <RenderValue data={item} depth={depth + 1} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // object
  if (isObject(parsed)) {
    return (
      <div className="space-y-4">
        {title && (
          <div className="text-sm font-bold uppercase tracking-wide text-gray-600">
            {formatKey(title)}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4">
          {Object.entries(parsed).map(([key, value]) => (
            <div
              key={key}
              className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
            >
              <div className="mb-4 flex items-center justify-between gap-2 border-b border-gray-100 pb-3">
                <div className="text-sm font-bold uppercase tracking-wide text-gray-500">
                  {formatKey(key)}
                </div>

                <div className="rounded-full bg-gray-100 px-2 py-1 text-[10px] font-semibold uppercase text-gray-500">
                  {Array.isArray(value)
                    ? "array"
                    : value === null
                      ? "null"
                      : typeof value}
                </div>
              </div>

              <RenderValue data={value} depth={depth + 1} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // fallback
  return (
    <pre className="overflow-auto rounded-xl bg-black p-4 text-sm text-green-400">
      {JSON.stringify(parsed, null, 2)}
    </pre>
  );
};

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export const TestRequest = () => {
  const [text, setText] = useState("");
  const [sample, setSample] = useState(1);

  const [systemText, setSystemText] = useState("");
  const [assistantText, setAssistantText] = useState("");

  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError("");
      setResponse(null);

      const body = {
        text,
        sample,
      };

      if (sample === 10) {
        body.system_text = systemText;
        body.assistant_text = assistantText;
      }

      const res = await fetchWithToken(
        "https://summarize45.curs.kz/summarize2",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        },
      );

      if (!res.ok) {
        throw new Error("Ошибка запроса");
      }

      const data = await res.json();

      setResponse(data);
    } catch (e) {
      console.error(e);
      setError("Не удалось отправить запрос");
    } finally {
      setLoading(false);
    }
  };

  const parsedResponse = useMemo(() => {
    if (!response) return null;

    const cloned = { ...response };

    if (cloned.summary) {
      cloned.summary = tryParseJson(cloned.summary);
    }

    return cloned;
  }, [response]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-gray-100 to-slate-200 px-4 py-10">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 rounded-3xl border border-white/50 bg-white/80 p-8 shadow-xl backdrop-blur">
          <h1 className="text-4xl font-black tracking-tight text-gray-800">
            Тестовый запрос
          </h1>

          <p className="mt-2 text-gray-500">
            Универсальный просмотрщик JSON-ответов
          </p>
        </div>

        {/* Form */}
        <div className="rounded-3xl border border-white/50 bg-white p-8 shadow-xl">
          <div className="space-y-8">
            <div>
              <label className="mb-3 block text-sm font-bold uppercase tracking-wide text-gray-600">
                Текст
              </label>

              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={10}
                placeholder="Введите текст..."
                className="w-full rounded-2xl border border-gray-300 bg-gray-50 p-5 text-sm outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
              />
            </div>

            <div>
              <label className="mb-3 block text-sm font-bold uppercase tracking-wide text-gray-600">
                Sample
              </label>

              <select
                value={sample}
                onChange={(e) => setSample(Number(e.target.value))}
                className="w-full rounded-2xl border border-gray-300 bg-gray-50 p-4 outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
                <option value={10}>10</option>
              </select>
            </div>

            {sample === 10 && (
              <div className="grid gap-6 lg:grid-cols-2">
                <div>
                  <label className="mb-3 block text-sm font-bold uppercase tracking-wide text-gray-600">
                    System Text
                  </label>

                  <textarea
                    value={systemText}
                    onChange={(e) => setSystemText(e.target.value)}
                    rows={6}
                    placeholder="Введите system_text..."
                    className="w-full rounded-2xl border border-gray-300 bg-gray-50 p-5 outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                  />
                </div>

                <div>
                  <label className="mb-3 block text-sm font-bold uppercase tracking-wide text-gray-600">
                    Assistant Text
                  </label>

                  <textarea
                    value={assistantText}
                    onChange={(e) => setAssistantText(e.target.value)}
                    rows={6}
                    placeholder="Введите assistant_text..."
                    className="w-full rounded-2xl border border-gray-300 bg-gray-50 p-5 outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                  />
                </div>
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 text-lg font-bold text-white shadow-lg transition-all hover:scale-[1.01] hover:from-blue-700 hover:to-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? "Отправка..." : "Отправить запрос"}
            </button>

            {error && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-600 shadow-sm">
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Response */}
        {parsedResponse && (
          <div className="mt-10 space-y-8">
            {parsedResponse?.usage && (
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-3xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-6 shadow-sm">
                  <div className="text-sm font-medium text-gray-500">
                    Prompt Tokens
                  </div>

                  <div className="mt-3 text-4xl font-black text-blue-700">
                    {parsedResponse.usage.prompt_tokens}
                  </div>
                </div>

                <div className="rounded-3xl border border-green-100 bg-gradient-to-br from-green-50 to-white p-6 shadow-sm">
                  <div className="text-sm font-medium text-gray-500">
                    Completion Tokens
                  </div>

                  <div className="mt-3 text-4xl font-black text-green-700">
                    {parsedResponse.usage.completion_tokens}
                  </div>
                </div>

                <div className="rounded-3xl border border-purple-100 bg-gradient-to-br from-purple-50 to-white p-6 shadow-sm">
                  <div className="text-sm font-medium text-gray-500">
                    Total Tokens
                  </div>

                  <div className="mt-3 text-4xl font-black text-purple-700">
                    {parsedResponse.usage.total_tokens}
                  </div>
                </div>

                <div className="rounded-3xl border border-orange-100 bg-gradient-to-br from-orange-50 to-white p-6 shadow-sm">
                  <div className="text-sm font-medium text-gray-500">
                    Cost USD
                  </div>

                  <div className="mt-3 break-all text-3xl font-black text-orange-700">
                    ${parsedResponse.usage.cost_usd}
                  </div>
                </div>
              </div>
            )}
            {/* Main response */}
            <div className="rounded-3xl border border-white/50 bg-white p-8 shadow-xl">
              <div className="mb-8 flex items-center justify-between gap-4 border-b border-gray-100 pb-6">
                <div>
                  <h2 className="text-3xl font-black text-gray-800">
                    Ответ сервера
                  </h2>

                  <p className="mt-1 text-sm text-gray-500">
                    Автоматический рендер любых JSON-структур
                  </p>
                </div>

                <div className="rounded-2xl bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700">
                  JSON Viewer
                </div>
              </div>

              <RenderValue data={parsedResponse} />
            </div>

            {/* Usage */}
          </div>
        )}
      </div>
    </div>
  );
};
